import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  fetchReport,
  listProperties,
  defaultDateRange,
  isoDate,
} from "@agencylens/connector-ga4";
import { renderGA4Report } from "@agencylens/pdf";
import { DEFAULT_AGENCY_BRAND } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import {
  loadConnectionRaw,
  readAccessToken,
  readRefreshToken,
  saveGoogleConnection,
  deleteConnection,
  isExpired,
} from "@/lib/connections.js";
import { connectGoogle, refreshGoogleToken } from "@/lib/oauth.js";
import { svgElementToPng } from "@/lib/chart-rasterize.js";
import { useApp } from "@/store/app.js";

const GA4_SCOPE = "https://www.googleapis.com/auth/analytics.readonly";

export function GA4Panel({
  onError,
  configReady,
}: {
  onError: (msg: string | undefined) => void;
  configReady: boolean;
}) {
  const {
    ga4Connection,
    ga4Properties,
    selectedPropertyId,
    lastGa4Report,
    setGa4Connection,
    setGa4Properties,
    setSelectedProperty,
    setLastGa4Report,
  } = useApp();

  const [busy, setBusy] = useState<string | undefined>();
  const [{ startDate, endDate }, setRange] = useState(() => defaultDateRange(30));
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const rec = await loadConnectionRaw("google-analytics", "default");
      if (rec) setGa4Connection(rec);
    })().catch((e) => onError(String(e)));
  }, [setGa4Connection, onError]);

  useEffect(() => {
    if (!ga4Connection) return;
    (async () => {
      try {
        setBusy("Loading GA4 properties…");
        const token = await ensureFreshToken();
        const props = await listProperties(token);
        setGa4Properties(props);
        if (!selectedPropertyId && props.length > 0) {
          setSelectedProperty(props[0]!.propertyId);
        }
      } catch (e) {
        onError(`Could not list GA4 properties: ${String(e)}`);
      } finally {
        setBusy(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ga4Connection?.updatedAt]);

  async function ensureFreshToken(): Promise<string> {
    const rec = await loadConnectionRaw("google-analytics", "default");
    if (!rec) throw new Error("No GA4 connection");
    if (!isExpired(rec)) return readAccessToken(rec);
    const refresh = await readRefreshToken(rec);
    if (!refresh) throw new Error("Token expired and no refresh token. Reconnect GA4.");
    const refreshed = await refreshGoogleToken(refresh);
    const newRec = await saveGoogleConnection("google-analytics", "default", refreshed);
    setGa4Connection(newRec);
    return readAccessToken(newRec);
  }

  async function onConnect() {
    onError(undefined);
    setBusy("Opening Google sign-in…");
    try {
      const tokens = await connectGoogle({ scope: GA4_SCOPE });
      const rec = await saveGoogleConnection("google-analytics", "default", tokens);
      setGa4Connection(rec);
    } catch (e) {
      onError(`GA4 OAuth failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDisconnect() {
    await deleteConnection("google-analytics", "default");
    setGa4Connection(undefined);
    setGa4Properties([]);
    setSelectedProperty(undefined);
    setLastGa4Report(undefined);
  }

  async function onGenerate() {
    if (!selectedPropertyId) return;
    const property = ga4Properties.find((p) => p.propertyId === selectedPropertyId);
    if (!property) return;
    onError(undefined);
    setBusy("Fetching GA4 data…");
    try {
      const token = await ensureFreshToken();
      const report = await fetchReport(token, property, startDate, endDate);
      setLastGa4Report(report);
    } catch (e) {
      onError(`GA4 fetch failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadPdf() {
    if (!lastGa4Report) return;
    onError(undefined);
    setBusy("Generating PDF…");
    try {
      const svgEl = chartContainerRef.current?.querySelector<SVGSVGElement>(
        "svg.recharts-surface",
      );
      let chartPng: Uint8Array | undefined;
      if (svgEl) {
        try {
          chartPng = await svgElementToPng(svgEl, 2);
        } catch (e) {
          console.warn("Chart rasterize failed; falling back to pdf-lib chart", e);
        }
      }
      const bytes = await renderGA4Report({
        data: lastGa4Report,
        agency: DEFAULT_AGENCY_BRAND,
        chartPng,
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencylens-ga4-report-${isoDate(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(`PDF generation failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <>
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-medium">Google Analytics 4</h2>
        {ga4Connection ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
            <span className="text-sm text-slate-500">
              {ga4Properties.length} {ga4Properties.length === 1 ? "property" : "properties"}{" "}
              available · expires {new Date(ga4Connection.expiresAt).toLocaleString()}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              disabled={busy !== undefined}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={onConnect} disabled={busy !== undefined || !configReady}>
            Connect Google Analytics
          </Button>
        )}
      </section>

      {ga4Connection && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium">Generate GA4 report</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Property</span>
              <select
                value={selectedPropertyId ?? ""}
                onChange={(e) => setSelectedProperty(e.target.value || undefined)}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              >
                <option value="">— select —</option>
                {ga4Properties.map((p) => (
                  <option key={p.propertyId} value={p.propertyId}>
                    {p.displayName} ({p.accountDisplayName})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setRange({ startDate: e.target.value, endDate })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setRange({ startDate, endDate: e.target.value })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={onGenerate}
              disabled={busy !== undefined || !selectedPropertyId}
            >
              Fetch data
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>
        </section>
      )}

      {lastGa4Report && (
        <>
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-medium">{lastGa4Report.property.displayName}</h2>
                <p className="text-sm text-slate-500">
                  {lastGa4Report.startDate} → {lastGa4Report.endDate}
                </p>
              </div>
              <Button onClick={onDownloadPdf} disabled={busy !== undefined}>
                Download GA4 PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Sessions" value={lastGa4Report.totals.sessions.toLocaleString("en-US")} />
              <StatCard label="Users" value={lastGa4Report.totals.totalUsers.toLocaleString("en-US")} />
              <StatCard label="Engaged sessions" value={lastGa4Report.totals.engagedSessions.toLocaleString("en-US")} />
              <StatCard label="Bounce rate" value={`${(lastGa4Report.totals.bounceRate * 100).toFixed(1)}%`} />
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Sessions over time</h3>
            <div ref={chartContainerRef} style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lastGa4Report.daily}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="sessions" stroke="#1d4ed8" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
